import { Cancel, Save } from '@mui/icons-material';
import { Divider, Grid, MenuItem, Switch, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import CustomButton, {
  CustomSelect,
  CustomTextField,
} from '../../../../Common/Components/Controls/InputControls';
import { Loader } from '../../../../Common/Components/Loader';
import {
  CardWrapper,
  FooterContainer,
  FooterWrapper,
  PaperWrapper,
} from '../../Common/CommonComponents';
import DynamicInputField from '../../Customer/Components/DynamicInputField';
import { farmService } from '../FarmService';
import { Farm, FarmTopologies, Resources } from '../Model/FarmModel';
import { showToast } from '../../../../lib/toast';

const FarmForm = () => {
  const initialGroupState: string[] = [];
  const initialTopologyState: FarmTopologies[] = [];
  const initialRegionState: string[] = [];
  const initialResourceState: Resources[] = [];
  const initialFarmsState: Farm[] = [];
  const history = useHistory();
  const [groups, setGroups] = useState(initialGroupState);
  const [topologies, setFarmTopologies] = useState(initialTopologyState);
  const [regions, setFarmRegions] = useState(initialRegionState);
  const [resources, setFarmResources] = useState(initialResourceState);
  const [multiinstancefarms, setFarms] = useState(initialFarmsState);
  const [ownership, setOwnership] = useState<string>();
  const [datadiskname, setDataDiskName] = useState<string>();
  const [disksize, setDiskSize] = useState<number>(1024);
  const [diskSKU, setDiskSKU] = useState<string>('Standard_LRS');
  const [isloading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState({
    Id: 0,
    DisplayName: '',
    GroupName: '',
    Location: '',
    Type: '',
    State: 7,
    DeploymentState: -1,
    SetupFilename: '',
    FarmRegion: 'northeurope',
    SecondaryRegion: 'westeurope',
    Properties: '',
    Subscription: '',
    IsHighAvailability: false,
  });

  useEffect(() => {
    loadFarmData().catch((err) => console.log(err));
    loadGroupsData().catch((err) => console.log(err));
    loadFarmTopologiesData().catch((err) => console.log(err));
    loadFarmRegionsData().catch((err) => console.log(err));
    loadFarmResourceData().catch((err) => console.log(err));
  }, []);

  const loadFarmData = async () => {
    const farmsReceived = await farmService.getFarms();
    setFarms(farmsReceived.Items);
  };

  const loadGroupsData = async () => {
    const groupsReceived = await farmService.getAvailableGroups();
    setGroups(groupsReceived);
  };
  const loadFarmTopologiesData = async () => {
    const farmsTopologiesReceived = await farmService.getFarmTopologies();
    setFarmTopologies(farmsTopologiesReceived.Items);
  };
  const loadFarmRegionsData = async () => {
    const farmsTopologiesReceived = await farmService.getFarmRegions();
    setFarmRegions(farmsTopologiesReceived);
  };
  const loadFarmResourceData = async () => {
    const farmsTopologiesReceived = await farmService.getResources();
    setFarmResources(farmsTopologiesReceived);
    setIsLoading(false);
  };

  const handleOwenerData = (e: {
    target: { value: React.SetStateAction<string | undefined> };
  }) => {
    setOwnership(e.target.value);
  };

  const [inputFields, setInputFields] = useState<
    { name: string; value: string | number }[]
  >([{ name: '', value: '' }]);

  const handleChangeInput = (
    index: number,
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    const values = [...inputFields];
    values[index][event.target.name as keyof (typeof inputFields)[0]] =
      event.target.value;
    setInputFields(values);
  };

  const handleSubmit = (e: { preventDefault: () => void }) => {
    e.preventDefault();
  };

  const handleAddFields = () => {
    setInputFields([...inputFields, { name: '', value: '' }]);
  };

  const handleRemoveFields = (index: number) => {
    const values = [...inputFields];
    values.splice(index, 1);
    setInputFields(values);
  };

  const Validate = () => {
    let res = true;

    if (formData.DisplayName === '') {
      showToast({
        type: 'error',
        title: 'Display Name is required',
      });
      res = false;
    }

    if (formData.GroupName === '') {
      showToast({
        type: 'error',
        title: 'Group Name is required',
      });

      res = false;
    }

    if (formData.Location === '') {
      showToast({
        type: 'error',
        title: 'Location is required',
      });

      res = false;
    }

    if (formData.SetupFilename === '') {
      showToast({
        type: 'error',
        title: 'Setup Filename is required',
      });

      res = false;
    }

    return res;
  };

  const handleCancel = () => {
    return history.push('/farm');
  };

  const getFarmId = () => {
    if (multiinstancefarms.length > 0) {
      return multiinstancefarms[multiinstancefarms.length - 1].Id + 1;
    }
    return 1;
  };

  const handleSave = async () => {
    if (Validate() === true) {
      setIsLoading(true);
      const properties: { [key: string]: string | number } = {};
      if (ownership !== undefined) {
        inputFields.push({ name: 'Ownership', value: ownership });
      }
      if (disksize !== undefined && diskSKU !== undefined) {
        inputFields.push({
          name: 'DataDiskName',
          value: datadiskname ?? 'Farm' + getFarmId() + 'IS-Data-IS-0',
        });
        inputFields.push({ name: 'DiskSizeGB', value: disksize });
        inputFields.push({ name: 'DiskSKU', value: diskSKU });
      }
      for (const element of inputFields) {
        const prop = element;
        if (!prop.name) continue;

        properties[prop.name] = prop.value;
      }
      const propertyString = JSON.stringify(properties);

      formData.Properties = propertyString;
      formData.Properties =
        Object.keys(properties).length > 0 ? propertyString : '{}';

      formData.Id = getFarmId();

      if (formData.Location.includes('@')) {
        const [farmType, Location] = formData.Location.split('@');
        formData.Location = Location;
        formData.Type = farmType;
        const topo = topologies.find((x) => x.FarmType === farmType);
        let subscription = '';
        if (topo === undefined) {
          subscription = farmType;
        } else {
          subscription = topo.DefaultSubscription;
        }
        formData.Subscription = subscription;
      }

      await farmService.postFarm(formData.Id, formData as Farm);
      setIsLoading(false);
      history.push('/farm');
    }
  };
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = event.target;
    if (checked !== undefined) {
      setFormData({
        ...formData,
        [name]: checked,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const updateFormData = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) =>
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });

  const {
    DisplayName,
    GroupName,
    Location,
    SetupFilename,
    FarmRegion,
    SecondaryRegion,
    IsHighAvailability,
  } = formData;

  return (
    <div>
      <PaperWrapper>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Create New Farm
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Loader loading={isloading} />
        <Grid container spacing={3}>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <CardWrapper>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                Basic Informations
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1">Display Name</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CustomTextField
                    value={DisplayName}
                    placeholder="DisplayName"
                    name="DisplayName"
                    onChange={(
                      e: React.ChangeEvent<
                        HTMLTextAreaElement | HTMLInputElement
                      >,
                    ) => updateFormData(e)}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} alignItems="center" mt={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1">Group Name</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CustomSelect
                    name="GroupName"
                    value={GroupName}
                    onChange={handleChange}
                    options={groups}
                    placeholder="Group Name"
                  >
                    {groups.map((group) => {
                      return (
                        <MenuItem key={group} value={group}>
                          {group}
                        </MenuItem>
                      );
                    })}
                  </CustomSelect>
                </Grid>
              </Grid>
              <Grid container spacing={2} alignItems="center" mt={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1">Location & farm type</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CustomSelect
                    name="Location"
                    value={Location}
                    onChange={handleChange}
                  >
                    {topologies.map((topology) => {
                      return (
                        <MenuItem
                          key={topology.FarmType}
                          value={topology.FarmType + '@' + topology.Location}
                        >
                          {topology.FarmType + '@' + topology.DisplayName}
                        </MenuItem>
                      );
                    })}
                  </CustomSelect>
                </Grid>
              </Grid>
              <Grid container spacing={2} alignItems="center" mt={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1">Assemble Build</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CustomSelect
                    name="SetupFilename"
                    value={SetupFilename}
                    onChange={handleChange}
                  >
                    {resources.map((resource) => (
                      <MenuItem
                        key={resource.Filename}
                        value={resource.Filename}
                      >
                        {resource.Filename}
                      </MenuItem>
                    ))}
                  </CustomSelect>
                </Grid>
              </Grid>
              <Grid container spacing={2} alignItems="center" mt={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1">Primary Region</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CustomSelect
                    name="FarmRegion"
                    value={FarmRegion}
                    onChange={handleChange}
                  >
                    {regions.map((region) => (
                      <MenuItem key={region} value={region}>
                        {region}
                      </MenuItem>
                    ))}
                  </CustomSelect>
                </Grid>
              </Grid>
              <Grid container spacing={2} alignItems="center" mt={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1">Secondary Region</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CustomSelect
                    name="SecondaryRegion"
                    value={SecondaryRegion}
                    onChange={handleChange}
                  >
                    {regions.map((region) => (
                      <MenuItem key={region} value={region}>
                        {region}
                      </MenuItem>
                    ))}
                  </CustomSelect>
                </Grid>
              </Grid>
              <Grid container spacing={2} alignItems="center" mt={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1">Ownership</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CustomTextField
                    name="ownership"
                    value={ownership ?? ''}
                    onChange={handleOwenerData}
                    required
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} alignItems="center" mt={2}>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  display="flex"
                  justifyContent={{ xs: 'center', sm: 'flex-start' }}
                >
                  <Typography variant="body1">High Availability</Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  display="flex"
                  justifyContent={{ xs: 'center', sm: 'flex-start' }}
                >
                  <Switch
                    checked={IsHighAvailability}
                    onChange={handleChange}
                    name="IsHighAvailability"
                  />
                </Grid>
              </Grid>
            </CardWrapper>
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <CardWrapper>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                Indexing Search
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={2} alignItems="center" mt={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1">Data Disk Name</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CustomTextField
                    value={
                      datadiskname ?? 'Farm' + getFarmId() + 'IS-Data-IS-0'
                    }
                    onChange={(e) => {
                      setDataDiskName(e.target.value);
                    }}
                    type="text"
                    name="DataDiskName"
                    required
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} alignItems="center" mt={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1">
                    Disk SKU (StorageType)
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CustomTextField
                    value={diskSKU}
                    onChange={(e) => {
                      setDiskSKU(e.target.value);
                    }}
                    type="text"
                    name="DiskSKU"
                    required
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} alignItems="center" mt={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1">Size (GiB)</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CustomTextField
                    value={disksize}
                    onChange={(e) => {
                      setDiskSize(Number(e.target.value));
                    }}
                    type="number"
                    name="DiskSize"
                    required
                    InputProps={{ inputProps: { min: 4, max: 32767 } }}
                  />
                </Grid>
              </Grid>
            </CardWrapper>
            <CardWrapper sx={{ mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                Farm Properties
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <form onSubmit={handleSubmit}>
                {inputFields.map((inputField, index) => (
                  <DynamicInputField
                    key={index}
                    index={index}
                    inputField={inputField}
                    handleChangeInput={handleChangeInput}
                    handleRemoveFields={handleRemoveFields}
                    handleAddFields={handleAddFields}
                  />
                ))}
              </form>
            </CardWrapper>
          </Grid>
        </Grid>
        <FooterWrapper>
          <FooterContainer>
            <Grid item xs={12} sm="auto">
              <CustomButton
                startIcon={<Save />}
                onClick={handleSave}
                label={'Save'}
              />
            </Grid>
            <Grid item xs={12} sm="auto">
              <CustomButton
                startIcon={<Cancel />}
                onClick={handleCancel}
                label={'Cancel'}
              />
            </Grid>
          </FooterContainer>
        </FooterWrapper>
      </PaperWrapper>
    </div>
  );
};

export default FarmForm;
